/**
 * @file Préchargement juste-à-temps de l'intent Stripe d'empreinte bancaire.
 * @module packages.common.components.table-reservation.hooks.usePrepareStripeIntent
 *
 * Source **unique** de création de l'intent Stripe côté widget. Activé dès que
 * l'utilisateur sort de la zone des paramètres qui impactent le montant
 * (service, date, créneau, convives, formules) et qu'il a saisi des
 * coordonnées exploitables à l'étape CONTACT, pour absorber la latence Stripe
 * (~300-600 ms) avant l'arrivée sur PAYMENT, où le `clientSecret` est alors
 * déjà dans `paymentConfig` et le formulaire de carte s'affiche sans loader.
 *
 * Attendre un contact valide (avec un court debounce de frappe à CONTACT)
 * permet d'attacher les coordonnées et le draftId à l'intent et au Customer
 * Stripe : un parcours abandonné reste identifiable côté Stripe, et un intent
 * échoué peut être rattaché à son brouillon par webhook. Les coordonnées ne
 * font pas partie de la clé de dédup : une correction ultérieure ne recrée
 * jamais d'intent.
 *
 * Invariants :
 *
 * 1. **Une seule source** — `PaymentStep` n'a plus sa propre logique de
 *    préparation : tout passe par ce hook. Évite tout risque de double
 *    création d'intent (deux endpoints `preparePayment` en parallèle créeraient
 *    deux intents orphelins chez Stripe).
 *
 * 2. **Activé par fenêtre d'étapes** — le hook n'agit que lorsque
 *    `currentStep` est dans `enabledAtSteps`. Avant cette fenêtre, on attend
 *    encore les paramètres ; après, on ne refait rien si l'intent est déjà
 *    prêt.
 *
 * 3. **Dédup par clé** — `preparedKeyRef` empêche de relancer l'appel Stripe
 *    quand `paymentConfig` est ré-écrit après merge (la réf change, mais le
 *    tuple service+date+créneau+convives+montant+source+formules reste le
 *    même). Sur erreur, la clé est relâchée pour autoriser un retry au
 *    prochain changement d'étape ou de paramètre.
 *
 * 4. **Garde-fou contre les réponses obsolètes** — si l'utilisateur modifie un
 *    paramètre amont pendant qu'un appel Stripe est en vol, `preparedKeyRef`
 *    aura été reset par `usePaymentRequirement` (qui vide `paymentConfig` à
 *    chaque dep change) ; la comparaison `preparedKeyRef.current !== key`
 *    après l'await ignore la réponse en retard.
 */
import {useEffect, useRef, useState} from 'react';
import {useReservationData} from 'packages/common/components/table-reservation/context/ReservationDataContext';
import {logger} from 'packages/common/utils/clientLogger';
import {BASE_STEPS} from '../constants/steps';
import {hasValidContact} from '../utils/contactValidity';
import {PAYMENT_ERROR_STAGES} from '../utils/paymentSignal';

/** Délai de frappe à CONTACT avant de figer les coordonnées envoyées à Stripe */
const CONTACT_DEBOUNCE_MS = 1500;

/**
 * Formate une date (`Date` ou chaîne ISO) au format `YYYY-MM-DD` en timezone
 * locale du client. Identique à `usePaymentRequirement.toIsoDate` ; centralisé
 * dans un util si l'on doit le partager avec un 3e consommateur.
 *
 * @param {Date|string|null|undefined} value - Date à formater
 * @return {string|null} Date au format `YYYY-MM-DD`, ou `null` si invalide
 */
function toIsoDate(value) {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return null;
}

/**
 * Précharge l'intent Stripe en background dès que l'utilisateur entre dans la
 * fenêtre d'étapes autorisées (par défaut : CONTACT et au-delà).
 *
 * @param {Object} params
 * @param {Object} params.formData - État courant du formulaire de réservation
 * @param {Function} params.updateFormData - Mutateur `(field, value) => void`
 * @param {string} params.currentStep - Étape logique courante (`BASE_STEPS.*`)
 * @param {string[]} params.enabledAtSteps - Étapes où le préchargement s'active
 * @param {Function} [params.t] - Fonction de traduction (pour le message d'erreur)
 * @param {Function} [params.onPrepareError] - Enregistreur d'erreur du signal empreinte
 *     `(stage, error) => void` (cf. usePaymentSignalTracker)
 * @param {string} [params.currentLanguage] - Langue du parcours, attachée à l'intent
 * @return {Object} `{prepareError}` — message d'erreur à afficher dans
 *     `PaymentStep`, ou `null` si tout va bien ou si l'effet est en attente.
 */
export function usePrepareStripeIntent({
  formData,
  updateFormData,
  currentStep,
  enabledAtSteps,
  t,
  onPrepareError,
  currentLanguage,
}) {
  const {preparePayment} = useReservationData();
  const [prepareError, setPrepareError] = useState(null);
  const preparedKeyRef = useRef(null);

  const paymentConfig = formData.paymentConfig;
  const {draftId, firstName, lastName, email, phone} = formData;
  const contactReady = hasValidContact({firstName, lastName, email, phone});

  // Coordonnées lues via une ref au moment de l'envoi : elles enrichissent
  // l'intent sans faire partie des dépendances qui relancent l'effet
  const traceRef = useRef({});
  traceRef.current = {
    draftId,
    language: currentLanguage,
    customerDetails: {firstName, lastName, email, phone},
  };

  // Créneau perdu à la validation : l'intent courant est consommé ou obsolète.
  // La clé est relâchée pour qu'un nouvel intent soit préparé même si le client
  // revient sur le même créneau (plafond relevé entre-temps, par exemple).
  useEffect(() => {
    if (formData.slotLost) preparedKeyRef.current = null;
  }, [formData.slotLost]);

  useEffect(() => {
    // Hors de la fenêtre d'activation : on ne fait rien, on n'efface pas
    // l'erreur éventuelle (l'utilisateur peut revenir à PAYMENT pour la voir).
    if (!enabledAtSteps.includes(currentStep)) return undefined;

    if (!paymentConfig || !paymentConfig.required) return undefined;
    if (paymentConfig.clientSecret) return undefined;

    const dateString = toIsoDate(formData.date);
    if (!dateString || !formData.timeSlot || !formData.service || !formData.partySize) {
      return undefined;
    }
    if (!contactReady) return undefined;

    const selectedFormulas = Array.isArray(formData.selectedFormulas) ?
      formData.selectedFormulas.map((f) => ({formulaId: f.formulaId, quantity: f.quantity || 1})) :
      [];

    const key = [
      formData.service,
      dateString,
      formData.timeSlot,
      formData.partySize,
      paymentConfig.amount,
      paymentConfig.source,
      JSON.stringify(selectedFormulas),
    ].join('|');

    if (preparedKeyRef.current === key) return undefined;

    // À CONTACT, le contact devient valide en cours de frappe : on laisse la
    // saisie se stabiliser avant de figer les coordonnées envoyées à Stripe
    const delay = currentStep === BASE_STEPS.CONTACT ? CONTACT_DEBOUNCE_MS : 0;

    const timer = setTimeout(async () => {
      preparedKeyRef.current = key;
      setPrepareError(null);

      try {
        const result = await preparePayment({
          numberOfGuests: formData.partySize,
          dateTime: `${dateString}T${formData.timeSlot}:00`,
          service: formData.service,
          selectedFormulas: selectedFormulas.length > 0 ? selectedFormulas : undefined,
          ...traceRef.current,
        });

        // Garde-fou contre réponses obsolètes (l'utilisateur a changé un
        // paramètre amont pendant que la requête était en vol).
        if (preparedKeyRef.current !== key) return;

        if (result?.success && result.data?.required) {
          // Merge : conserve la décision (cancellationDeadline, mode, etc.) et
          // ajoute les champs Stripe (clientSecret, stripeAccount, intentType…).
          updateFormData('paymentConfig', {...paymentConfig, ...result.data});
        } else {
          // Le backend a changé d'avis entre evaluate et prepare (rare).
          updateFormData('paymentRequired', false);
        }
      } catch (err) {
        if (preparedKeyRef.current !== key) return;
        logger.error('Erreur lors de la préparation du paiement Stripe :', err);
        if (onPrepareError) onPrepareError(PAYMENT_ERROR_STAGES.PREPARE, err);
        setPrepareError(
            (t && t('widgets.reservation.payment.prepareError')) ||
            'Impossible de préparer le paiement. Merci de réessayer dans quelques instants.',
        );
        // Relâche la clé pour autoriser un retry au prochain changement
        // d'étape ou de paramètre amont.
        preparedKeyRef.current = null;
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [
    currentStep,
    enabledAtSteps,
    paymentConfig,
    contactReady,
    formData.service,
    formData.date,
    formData.timeSlot,
    formData.partySize,
    formData.selectedFormulas,
    preparePayment,
    updateFormData,
    t,
    onPrepareError,
  ]);

  return {prepareError};
}
